import { BrowserApp } from "./BrowserApp.js";

const DEFAULT_AGENT_TAB = "agent";

/**
 * @typedef {{
 *   isOrphan: Function,
 *   getAgentUrl: Function,
 *   getTokenBase64: Function,
 *   getParty: Function,
 *   getToken: Function,
 * }} BrowserChatApp
 */

/**
 * @typedef {{
 *   events?: object[],
 *   clientToolCalls?: Array<{ id: string, name: string, input?: object }>,
 * }} ConversationRound
 */

export class ClientToolRegistry {
  /** @type {Map<string, { descriptor: object, execute: Function }>} */
  #tools = new Map();

  /**
   * @param {string} name
   * @param {object} descriptor
   * @param {Function} execute
   * @return {ClientToolRegistry}
   */
  register(name, descriptor, execute) {
    this.#tools.set(name, { descriptor, execute });
    return this;
  }

  /**
   * @return {object[]}
   */
  descriptors() {
    return [...this.#tools.values()].map((tool) => tool.descriptor);
  }

  /**
   * @param {Array<{ id: string, name: string, input?: object }>} toolCalls
   * @param {object=} context
   * @return {Promise<Array<object>>}
   */
  async executeToolCalls(toolCalls, context = {}) {
    const results = [];

    for (const call of toolCalls) {
      const clientTool = this.#tools.get(call.name);
      if (!clientTool) {
        results.push({
          toolCallId: call.id,
          name: call.name,
          content: `No client tool named ${call.name}`,
          isError: true,
        });
        continue;
      }

      try {
        const content = await clientTool.execute(call.input || {}, context);
        results.push({
          toolCallId: call.id,
          name: call.name,
          content: JSON.stringify(content),
        });
      } catch (e) {
        results.push({
          toolCallId: call.id,
          name: call.name,
          content: String(e),
          isError: true,
        });
      }
    }

    return results;
  }
}

export class GenerativeChatClient {
  #app;
  #appName;
  #agentTab;
  #agentUrl = null;
  #clientTools;

  /**
   * @param {{
   *   app?: BrowserChatApp,
   *   appName?: string,
   *   agentTab?: string,
   *   clientTools?: ClientToolRegistry,
   * }=} options
   */
  constructor(options = {}) {
    this.#app = options.app;
    this.#appName = options.appName;
    this.#agentTab = options.agentTab ?? DEFAULT_AGENT_TAB;
    this.#clientTools = options.clientTools ?? new ClientToolRegistry();
  }

  /**
   * @return {BrowserChatApp | null}
   */
  get app() {
    return this.#app ?? null;
  }

  /**
   * @return {string | null}
   */
  get agentUrl() {
    return this.#agentUrl;
  }

  /**
   * @return {ClientToolRegistry}
   */
  get clientTools() {
    return this.#clientTools;
  }

  /**
   * @return {Promise<GenerativeChatClient>}
   */
  async init() {
    this.#app ??= await BrowserApp.getInstance(this.#appName);
    if (!this.#app.isOrphan()) {
      this.#agentUrl = await this.#app.getAgentUrl(this.#agentTab);
    }
    return this;
  }

  /**
   * @return {boolean}
   */
  isOrphan() {
    return Boolean(this.#app?.isOrphan());
  }

  /**
   * @return {string}
   */
  getParty() {
    return this.#requireApp().getParty();
  }

  /**
   * @param {{ targetParty: string }} options
   * @return {Promise<object>}
   */
  listChats({ targetParty }) {
    return this.postAgent("/chats", { targetParty });
  }

  /**
   * List the models a chat may be created with. Resolves to `{ models }`, where
   * each model is `{ id, displayName, transport, capabilities: { thinking } }`.
   * Use a model's `capabilities` to render the right controls (e.g. only offer
   * an Extended Thinking toggle when `capabilities.thinking` is not `'none'`).
   *
   * @param {{ targetParty: string }} options
   * @return {Promise<object>}
   */
  listModels({ targetParty }) {
    return this.postAgent("/models", { targetParty });
  }

  /**
   * @param {{
   *   targetParty: string,
   *   chatId?: string | null,
   *   systemPrompt?: string,
   *   temperature?: number,
   *   model?: string,
   * }} options
   * @return {Promise<object>}
   */
  createSession(options) {
    return this.postAgent("/session", {
      targetParty: options.targetParty,
      ...(options.chatId ? { chatId: options.chatId } : {}),
      systemPrompt: options.systemPrompt,
      temperature: options.temperature,
      ...(options.model ? { model: options.model } : {}),
      clientTools: this.#clientTools.descriptors(),
    });
  }

  /**
   * @param {{
   *   targetParty: string,
   *   chatId: string,
   *   message: string,
   *   temperature?: number,
   *   toolsEnabled?: boolean,
   *   toolDetails?: boolean,
   *   onConversationResult?: Function,
   *   onClientToolCalls?: Function,
   *   onEvent?: Function,
   * }} options
   * @return {Promise<object>}
   */
  async sendMessage(options) {
    const onEvent = options.onEvent;
    /** @type {ConversationRound} */
    let result = onEvent
      ? await this.postAgentStream("/send", {
        targetParty: options.targetParty,
        chatId: options.chatId,
        message: options.message,
        temperature: options.temperature,
        toolsEnabled: options.toolsEnabled,
        toolDetails: options.toolDetails,
      }, onEvent)
      : await this.postAgent("/send", {
        targetParty: options.targetParty,
        chatId: options.chatId,
        message: options.message,
        temperature: options.temperature,
        toolsEnabled: options.toolsEnabled,
        toolDetails: options.toolDetails,
      });
    await options.onConversationResult?.(result);

    while (result.clientToolCalls?.length) {
      await options.onClientToolCalls?.(result.clientToolCalls);
      const toolResults = await this.#clientTools.executeToolCalls(
        result.clientToolCalls,
        { targetParty: options.targetParty },
      );
      result = await this.sendClientToolResults({
        targetParty: options.targetParty,
        chatId: options.chatId,
        toolResults,
        temperature: options.temperature,
        toolsEnabled: options.toolsEnabled,
        toolDetails: options.toolDetails,
        onEvent,
      });
      await options.onConversationResult?.(result);
    }

    return result;
  }

  /**
   * @param {{
   *   targetParty: string,
   *   chatId: string,
   *   toolResults: Array<object>,
   *   temperature?: number,
   *   toolsEnabled?: boolean,
   *   toolDetails?: boolean,
   *   onEvent?: Function,
   * }} options
   * @return {Promise<object>}
   */
  sendClientToolResults(options) {
    const body = {
      targetParty: options.targetParty,
      chatId: options.chatId,
      toolResults: options.toolResults,
      temperature: options.temperature,
      toolsEnabled: options.toolsEnabled,
      toolDetails: options.toolDetails,
    };
    return options.onEvent
      ? this.postAgentStream("/client-tool-results", body, options.onEvent)
      : this.postAgent("/client-tool-results", body);
  }

  /**
   * @param {{ targetParty: string, chatId: string }} options
   * @return {Promise<object>}
   */
  closeChat({ targetParty, chatId }) {
    return this.postAgent("/close", { targetParty, chatId });
  }

  /**
   * @param {{ targetParty: string, chatId: string }} options
   * @return {Promise<object>}
   */
  deleteChat({ targetParty, chatId }) {
    return this.postAgent("/delete", { targetParty, chatId });
  }

  /**
   * @param {string} path
   * @param {object} body
   * @return {Promise<object>}
   */
  async postAgent(path, body) {
    if (!this.#agentUrl) throw "Agent URL is not available";
    const response = await fetch(`${this.#agentUrl}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Tabserver-Token": this.#requireApp().getTokenBase64(),
      },
      body: JSON.stringify(body),
    });

    const json = await response.json();
    if ("error" in json) throw json.error;
    return json.ok;
  }

  /**
   * POSTs like {@link postAgent} but consumes the NDJSON streaming response the
   * agent sends when `Accept: application/x-ndjson` is set: each `event` frame is
   * delivered to `onEvent` the moment it arrives. Accumulates the events and the
   * terminal frame's `clientToolCalls` into the same `ConversationRound` shape
   * `postAgent` returns, so callers stay uniform. An `error` frame throws to match
   * `postAgent`'s error contract.
   * @param {string} path
   * @param {object} body
   * @param {Function} [onEvent]
   * @return {Promise<ConversationRound>}
   */
  async postAgentStream(path, body, onEvent) {
    if (!this.#agentUrl) throw "Agent URL is not available";
    const response = await fetch(`${this.#agentUrl}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/x-ndjson",
        "X-Tabserver-Token": this.#requireApp().getTokenBase64(),
      },
      body: JSON.stringify(body),
    });
    if (!response.body) throw "Streaming response has no body";

    /** @type {ConversationRound} */
    const round = { events: [] };
    /** @param {string} line */
    const handleLine = async (line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const frame = JSON.parse(trimmed);
      if (frame.type === "event") {
        round.events?.push(frame.event);
        await onEvent?.(frame.event);
      } else if (frame.type === "done") {
        if (frame.clientToolCalls?.length) {
          round.clientToolCalls = frame.clientToolCalls;
        }
      } else if (frame.type === "error") {
        throw frame.message;
      }
    };

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let index;
      while ((index = buffer.indexOf("\n")) >= 0) {
        const line = buffer.slice(0, index);
        buffer = buffer.slice(index + 1);
        await handleLine(line);
      }
    }
    buffer += decoder.decode();
    await handleLine(buffer);

    return round;
  }

  /**
   * @param {unknown} value
   * @return {string}
   */
  normalizeTargetParty(value) {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) throw "Daemon host is required";

    try {
      const url = trimmed.includes("://")
        ? new URL(trimmed)
        : new URL(`${globalThis.location.protocol}//${trimmed}`);
      if (!url.host) throw "missing host";
      return url.host;
    } catch (_e) {
      throw `Invalid daemon host: ${value}`;
    }
  }

  /**
   * @return {BrowserChatApp}
   */
  #requireApp() {
    if (!this.#app) throw "Browser app is not initialised";
    return this.#app;
  }
}

/**
 * @param {BrowserChatApp=} app
 * @return {ClientToolRegistry}
 */
export function createDefaultBrowserChatTools(app = undefined) {
  const registry = new ClientToolRegistry();
  registry.register(
    "browser_daemon_identity",
    {
      type: "function",
      name: "browser_daemon_identity",
      execution: "client",
      description:
        "Get the active chat daemon and the counterparty it is talking to, derived from the browser launch token and active chat target.",
      parameters: {
        type: "object",
        properties: {},
        additionalProperties: false,
      },
    },
    /**
     * @param {object} _input
     * @param {{ targetParty?: string, targetAud?: string }} context
     */
    async (_input, context = {}) => {
      const browserApp = app ?? await BrowserApp.getInstance();
      const token = browserApp.getToken();
      const party = context.targetParty || token.getParty();
      const aud = context.targetAud ||
        `${globalThis.location.protocol}//${party}`;

      return {
        aud,
        sub: token.getSub(),
        party,
        counterparty: token.getCounterparty(),
        src: token.getSrc(),
      };
    },
  );
  registry.register(
    "browser_language",
    {
      type: "function",
      name: "browser_language",
      execution: "client",
      description: "Get the browser language preferences for this page.",
      parameters: {
        type: "object",
        properties: {},
        additionalProperties: false,
      },
    },
    () => ({
      language: navigator.language,
      languages: Array.from(navigator.languages ?? []),
    }),
  );
  registry.register(
    "browser_time",
    {
      type: "function",
      name: "browser_time",
      execution: "client",
      description: "Get the current timezone and local time from the browser.",
      parameters: {
        type: "object",
        properties: {},
        additionalProperties: false,
      },
    },
    () => {
      const now = new Date();
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      return {
        timeZone,
        isoTime: now.toISOString(),
        localTime: now.toLocaleString(undefined, { timeZone }),
        offsetMinutes: -now.getTimezoneOffset(),
      };
    },
  );
  return registry;
}
